// Single Disease Group card (PRD 08 §2.2 #2-#3).
// Edit/Delete buttons are always visible (matches prototype, overrides PRD §6
// hover-only spec per stakeholder review). Hover still raises the card
// (background swap + 1px lift) for visual feedback.
// Selection state owned by `useDiseaseCodePageStore`; CRUD wiring lives in
// `DiseaseGroupListPanel`. PRD §7 (Req Clarification Q3) — restricted actions stay VISIBLE but
// disabled for READ users, never hidden; PRD 21 §7.4 P-2 supplies the wording.

import { useTranslation } from 'react-i18next';
import { Folder, Pencil, Trash2 } from 'lucide-react';

import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

import type { DiseaseGroup } from '../types/disease-code.types';

interface DiseaseGroupCardProps {
  group: DiseaseGroup;
  selected: boolean;
  onSelect: (id: number) => void;
  onEdit: (group: DiseaseGroup) => void;
  onDelete: (group: DiseaseGroup) => void;
  // PRD §7 — Edit/Delete require METADATA:WRITE. When false the action row
  // disappears so READ users see a cleaner card.
  canWrite: boolean;
  codeCountLabel: string;
}

export function DiseaseGroupCard({
  group,
  selected,
  onSelect,
  onEdit,
  onDelete,
  canWrite,
  codeCountLabel,
}: DiseaseGroupCardProps) {
  const { t } = useTranslation('admin');
  const { denyProps } = useWriteDenyGuard(canWrite);
  // SHCT-582 (QC re-test) — PRD 21 §7.4 **P-2** is `버튼 disabled + 툴팁`, 후속 동작 없음. These two
  // used to be `aria-disabled` only, so an ordinary click still reached the handler and answered
  // with P-3's toast — P-3's channel on a P-2 situation, and invisible to the L2 guard, which can
  // only recognise a real `disabled`. A disabled button also dispatches no click at all, so the
  // `stopPropagation` that kept the surrounding card from selecting is no longer load-bearing here.

  return (
    <>
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(group.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(group.id);
        }
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = 'var(--accent)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = selected
          ? 'var(--badge-primary-bg)'
          : 'var(--background)';
        e.currentTarget.style.transform = '';
      }}
      style={{
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${selected ? 'var(--info)' : 'transparent'}`,
        borderRadius: 'var(--radius-md)',
        background: selected ? 'var(--badge-primary-bg)' : 'var(--background)',
        padding: '10px 12px',
        cursor: 'pointer',
        transition: 'border-color .15s, background .15s, transform .15s',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 6,
        }}
      >
        <Folder size={14} />
        <span>{group.name}</span>
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--muted-foreground)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span>
          {group.rangeStart} ~ {group.rangeEnd}
        </span>
        <span>{codeCountLabel}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          disabled={!canWrite}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(group);
          }}
          {...denyProps}
        >
          <Pencil size={12} />
          {t('diseaseCodeManagement.groupPanel.edit')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          disabled={!canWrite}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(group);
          }}
          {...denyProps}
        >
          <Trash2 size={12} />
          {t('diseaseCodeManagement.groupPanel.delete')}
        </button>
      </div>
    </div>
    </>
  );
}
